/**
 * CustomerPortalApp
 *
 * Wrapper für alle /customer/*-Routen.
 * Beim Mounten: /api/customer/me abrufen.
 *   - Erfolg                → Store befüllen, Outlet rendern
 *   - Server nicht erreichbar → warten und erneut fragen (Deploy, Neustart)
 *   - sonst (401 & Co.)     → zu /customer/login weiterleiten
 */
import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { getCustomerMe } from '../../lib/api.customer';
import { isServerUnavailableError } from '../../lib/api/http';
import { useCustomerStore } from '../../stores/customerStore';

const PUBLIC_PATHS = ['/customer/login', '/customer/verify'];

/** Server startet neu: so oft erneut fragen, solange der Kunde vor dem Lader wartet. */
const SERVER_RECHECK_MS = 5000;

export function CustomerPortalApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [serverRestarting, setServerRestarting] = useState(false);
  const contact = useCustomerStore((s) => s.contact);
  const setContact = useCustomerStore((s) => s.setContact);

  const isPublicRoute = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p));

  // Session-Expiry Toast
  useEffect(() => {
    const handleSessionInvalid = () => {
      const wasPreview = !!useCustomerStore.getState().contact?.preview;
      notifications.show(
        wasPreview
          ? {
              title: 'Mitarbeiter-Ansicht beendet',
              message: 'Die Ansicht ist abgelaufen. Im ServiceHub lässt sie sich neu öffnen.',
              color: 'orange',
              autoClose: 8000,
            }
          : {
              title: 'Sitzung abgelaufen',
              message: 'Bitte erneut anmelden.',
              color: 'orange',
              autoClose: 5000,
            }
      );
      useCustomerStore.getState().clear();
      navigate('/customer/login', { replace: true });
    };
    window.addEventListener('customer-portal:session-invalid', handleSessionInvalid);
    return () => window.removeEventListener('customer-portal:session-invalid', handleSessionInvalid);
  }, [navigate]);

  // Rate-Limit Toast (3.2)
  useEffect(() => {
    const handleRateLimited = () => {
      notifications.show({
        title: 'Zu viele Anfragen',
        message: 'Bitte warte einen Moment und versuche es erneut.',
        color: 'yellow',
        autoClose: 5000,
      });
    };
    window.addEventListener('customer-portal:rate-limited', handleRateLimited);
    return () => window.removeEventListener('customer-portal:rate-limited', handleRateLimited);
  }, []);

  useEffect(() => {
    if (isPublicRoute) {
      setChecking(false);
      return;
    }

    // Wenn Store schon befüllt → kein erneuter Fetch
    if (contact) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const check = () => {
      getCustomerMe()
        .then((me) => {
          if (cancelled) return;
          setServerRestarting(false);
          setContact(me);
          setChecking(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          // Server startet gerade neu (Deploy) oder Netz weg: Das sagt nichts über die Anmeldung.
          // Bis v1.355.0 ging es hier zur Anmeldeseite — wo sich in diesem Moment nicht einmal ein
          // Link anfordern ließ. Stattdessen warten und erneut fragen.
          if (isServerUnavailableError(error)) {
            setServerRestarting(true);
            retry = setTimeout(check, SERVER_RECHECK_MS);
            return;
          }
          useCustomerStore.getState().clear();
          setChecking(false);
          navigate(`/customer/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        });
    };
    check();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [isPublicRoute, location.pathname, navigate, contact, setContact]);

  if (checking && !isPublicRoute) {
    return (
      <Center h="100dvh">
        <Stack align="center" gap="sm">
          <Loader size="sm" color="orange" />
          <Text size="sm" c="dimmed" ta="center">
            {serverRestarting
              ? 'Das Portal startet gerade neu — einen Moment, es geht gleich weiter.'
              : 'Lade …'}
          </Text>
        </Stack>
      </Center>
    );
  }

  return <Outlet />;
}
